import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import AssetProfileCard from 'common/components/asset-profile-card';
import BuildingInfoCard from 'common/components/building-info-card';
import BuildingLocationViewCard from 'common/components/building-location-view-card';
import BuildingTrafficHeatMapCard from 'common/components/building-traffic-heatmap-card';
import {getUnitStringShort} from 'common/utils/chart-util';
import {cardsWithoutNameInputs} from 'common/utils/reports-util';
import Card from 'common/components/card';
import CardContent from 'common/components/card-content';
import CardHeader from 'common/components/card-header';
import ComparisonCard from 'common/components/comparison-card';
import CompetitionRankingsCard from 'common/components/competition-rankings-card';
import HeatMapCard from 'common/components/heatmap-card';
import ImageCard from 'common/components/image-card';
import IframeCard from 'common/components/iframe-card';
import MapCard from 'common/components/map-card';
import NoDataCard from 'common/components/no-data-card';
import ProjectProfileCard from 'common/components/project-profile-card';
import ReadoutCard from 'common/components/readout-card';
import StackedBreakdownCard from 'common/components/stacked-breakdown-card';
import SummaryCard from 'common/components/summary-card';
import TableCard from 'common/components/table-card';
import TenantInfoCard from 'common/components/tenant-info-card';
import TenantConsumptionCostCard from 'common/components/tenant-consumption-cost-card';
import TextCard from 'common/components/text-card';
import TrendsCard from 'common/components/trends-card';
import PointTrendsCard from 'common/components/point-trends-card';
import YearOverYearTrendsCard from 'common/components/year-over-year-trends-card';
import BuildingAnalyticsZoneFlowCard, {
  addColor,
  parseZoneFlowLinks,
  parseZoneFlowNodes,
} from 'common/components/building-analytics-zone-flow-card';

import {
  ReportChartTypeEnum,
  ReportDifferenceColorEnum,
  ReportTypesEnum,
} from 'common/proxies/enum-proxy';
import {t} from 'common/utils/translation-util';

export default class ReportCard extends React.Component {
  static propTypes = {
    animate: PropTypes.bool,
    connectDragSource: PropTypes.func,
    disableConfig: PropTypes.bool,
    forExport: PropTypes.bool,
    inStoryboard: PropTypes.bool,
    isDragging: PropTypes.bool,
    report: PropTypes.object.isRequired,
  };

  static defaultProps = {
    animate: false,
    connectDragSource: (x) => x,
    disableConfig: false,
    forExport: false,
    inStoryboard: false,
    isDragging: false,
  };

  constructor(props) {
    super(props);
    this.disableConfig = this.disableConfig.bind(this);
    this.onDashboardSetLayout = this.onDashboardSetLayout.bind(this);
    this.state = {
      lastLayoutUpdate: new Date(),
    };
  }

  componentDidMount() {
    window.addEventListener('Dashboard-setLayout', this.onDashboardSetLayout);
  }

  shouldComponentUpdate(nextProps, nextState) {
    // deep equality check, faster than deepEqual
    // see http://www.mattzeunert.com/2016/01/28/javascript-deep-equal.html
    const reportHasChanged =
      JSON.stringify(this.props.report) !== JSON.stringify(nextProps.report);
    const layoutChanged =
      nextState.lastLayoutUpdate !== this.state.lastLayoutUpdate;
    const shouldUpdate =
      nextProps.isDragging || reportHasChanged || layoutChanged;

    return shouldUpdate;
  }

  componentWillUnmount() {
    window.removeEventListener(
      'Dashboard-setLayout',
      this.onDashboardSetLayout,
    );
  }

  getReportCard(report) {
    const {errors, meta} = report;

    if (meta && meta.hasData === false) {
      return this.renderNoDataCard(report);
    }

    if (meta && meta.chartType) {
      if (meta.chartType.id === ReportChartTypeEnum.TABLE.id) {
        return this.renderTableCard(report);
      } else if (meta.chartType.id === ReportChartTypeEnum.STACKED_COLUMN.id) {
        return this.renderStackedBreakdownCard(report);
      }
    }

    if (errors?.length) {
      return this.renderErrorCard(report);
    }

    try {
      switch (report.type.id) {
        case ReportTypesEnum.ORG_TRENDS.id:
        case ReportTypesEnum.TENANT_TRENDS.id:
        case ReportTypesEnum.BUILDING_TRENDS.id:
          return this.renderTrendsCard(report);
        case ReportTypesEnum.BUILDING_COMPARISON.id:
        case ReportTypesEnum.TENANT_COMPARISON.id:
        case ReportTypesEnum.PORTFOLIO_COMPARISONS.id:
          return this.renderComparisonCard(report);
        case ReportTypesEnum.BUILDING_SUMMARY.id:
        case ReportTypesEnum.TENANT_SUMMARY.id:
          return this.renderSummaryCard(report);
        case ReportTypesEnum.BUILDING_YOY_TRENDS.id:
        case ReportTypesEnum.TENANT_YOY_TRENDS.id:
        case ReportTypesEnum.PORTFOLIO_YOY_TRENDS.id:
          return this.renderYearOverYearTrendsCard(report);
        case ReportTypesEnum.BUILDING_INFO.id:
          return this.renderBuildingInfo(report);
        case ReportTypesEnum.POINT_TRENDS.id:
          return this.renderPointTrendsCard(report);
        case ReportTypesEnum.IMAGE.id:
          return this.renderImageCard(report);
        case ReportTypesEnum.STACKED_BREAKDOWN.id:
          return this.renderStackedBreakdownCard(report);
        case ReportTypesEnum.TENANT_COST_TABLE.id:
          return this.renderTenantConsumptionCostCard(report);
        case ReportTypesEnum.TENANT_INFO.id:
          return this.renderTenantInfo(report);
        case ReportTypesEnum.TEXT.id:
          return this.renderTextCard(report);
        case ReportTypesEnum.COMPETITION_RANKINGS.id:
          return this.renderCompetitionRankingsCard(report);
        case ReportTypesEnum.READOUT.id:
          return this.renderReadoutCard(report);
        case ReportTypesEnum.IFRAME.id:
          return this.renderIframeCard(report);
        case ReportTypesEnum.MAP.id:
          return this.renderMapCard(report);
        case ReportTypesEnum.HEAT_MAP.id:
          return this.renderHeatMapCard(report);
        case ReportTypesEnum.ASSET_PROFILE.id:
          return this.renderAssetProfileCard(report);
        case ReportTypesEnum.PROJECT_PROFILE.id:
          return this.renderProjectProfileCard(report);
        case ReportTypesEnum.BUILDING_TRAFFIC_HEATMAP.id:
          return this.renderBuildingTrafficHeatMapCard(report);
        case ReportTypesEnum.BUILDING_LOCATION_VIEW.id:
          return this.renderBuildingLocationViewCard(report);
        case ReportTypesEnum.BUILDING_ANALYTICS_ZONE_FLOW.id:
          return this.renderBuildingAnalyticsZoneFlowCard(report);
        default:
          return this.renderErrorCard(
            report,
            'Report type not found in ReportTypesEnum',
          );
      }
    } catch (error) {
      return this.renderErrorCard(report, error);
    }
  }

  disableConfig() {
    return (
      this.props.disableConfig ||
      (this.props.report && !this.props.report.id) ||
      this.props.forExport ||
      false
    );
  }

  renderAssetProfileCard(report) {
    return (
      <AssetProfileCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderBuildingTrafficHeatMapCard(report) {
    const {
      blurLevel,
      cardHeightAsImage,
      customCardHeight,
      floor,
      geojsonUrl,
      legendColorScale,
      legendMaxValue,
      legendMinValue,
      mapBearingAngle,
      mapCenter,
      mapPitchAngle,
      zoom,
    } = report.meta;
    return (
      <BuildingTrafficHeatMapCard
        connectDragSource={this.props.connectDragSource}
        customCardHeight={!cardHeightAsImage ? customCardHeight : undefined}
        disableConfig={this.disableConfig()}
        floor={floor}
        forExport={this.props.forExport}
        geojsonUrl={geojsonUrl}
        lastLayoutUpdate={moment(this.state.lastLayoutUpdate).toISOString()}
        legendColorScale={legendColorScale}
        legendMaxValue={legendMaxValue || undefined}
        legendMinValue={legendMinValue || undefined}
        mapBearingAngle={mapBearingAngle}
        mapBlurLevel={blurLevel || undefined}
        mapCenter={mapCenter}
        mapPitchAngle={mapPitchAngle}
        mapZoom={zoom || undefined}
        report={report}
      />
    );
  }

  renderBuildingInfo(report) {
    return (
      <BuildingInfoCard
        buildingInfo={report.meta}
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderBuildingLocationViewCard(report) {
    const {
      cardHeightAsImage,
      customCardHeight,
      floor,
      geojsonUrl,
      mapBearingAngle,
      mapCenter,
      mapPitchAngle,
      zoom,
    } = report.meta;

    return (
      <BuildingLocationViewCard
        attributes={report.meta.attributes}
        connectDragSource={this.props.connectDragSource}
        customCardHeight={!cardHeightAsImage ? customCardHeight : undefined}
        disableConfig={this.disableConfig()}
        floor={floor}
        geojsonUrl={geojsonUrl}
        lastLayoutUpdate={moment(this.state.lastLayoutUpdate).toISOString()}
        mapBearingAngle={mapBearingAngle}
        mapCenter={mapCenter}
        mapPitchAngle={mapPitchAngle}
        mapZoom={zoom || undefined}
        report={report}
      />
    );
  }

  renderComparisonCard(report) {
    return (
      <ComparisonCard
        animate={this.props.animate}
        building={'building'}
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
        showIfBlank={true}
      />
    );
  }

  renderCompetitionRankingsCard(report) {
    return (
      <CompetitionRankingsCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderErrorCard(report, err) {
    if (err) {
      console.error(err);
    }
    return this.props.connectDragSource(
      <div>
        <Card>
          <CardHeader
            errors={report.errors}
            priorityWarnings={report.priorityWarnings}
            reportId={report.id}
            subtitle={report.type?.displayName || t('Unknown Type')}
            title={report.name || t('Unnamed')}
            warnings={report.warnings}
          />
          <CardContent>
            {Array.isArray(report.errors)
              ? report.errors.map((error) => <p key={error}>{error}</p>)
              : t('Configuration error')}
          </CardContent>
        </Card>
      </div>,
    );
  }

  renderHeatMapCard(report) {
    return (
      <HeatMapCard
        appUrl={report.meta.appUrl}
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderIframeCard(report) {
    return (
      <IframeCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        disableHyperlinks={report.data.disable_links}
        height={report.data.card_height}
        report={report}
        scrolling={report.data.scrolling}
        src={report.data.url}
      />
    );
  }

  renderImageCard(report) {
    const meta = report.meta;
    return (
      <ImageCard
        backgroundColor={meta.backgroundColor}
        backgroundPosition={meta.backgroundPosition}
        backgroundSize={meta.backgroundSize}
        caption={meta.caption}
        captionDescription={meta.captionDescription}
        captionDescriptionFontSize={meta.captionDescriptionFontSize}
        captionDescriptionTargetBlank={meta.captionDescriptionTargetBlank}
        captionTitle={meta.captionTitle}
        captionTitleFontSize={meta.captionTitleFontSize}
        cardHeightAsImage={meta.cardHeightAsImage}
        connectDragSource={this.props.connectDragSource}
        customCardHeight={meta.customCardHeight}
        disableConfig={this.disableConfig()}
        imageAlt={meta.imageAlt}
        imageHeight={report.data.images[0].height}
        imageWidth={report.data.images[0].width}
        reportId={report.id}
        src={report.data.images[0].src}
        srcParams={report.srcParams || ''}
      />
    );
  }

  renderMapCard(report) {
    return (
      <MapCard
        buildings={report.meta.dataSummary.buildings}
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        forExport={this.props.forExport}
        height={report.meta.height}
        inStoryboard={this.props.inStoryboard}
        meta={report.meta}
        priorityWarnings={report.priorityWarnings}
        reportId={report.id}
        title={report.name}
        warnings={report.warnings}
      />
    );
  }

  renderNoDataCard(report) {
    const title =
      cardsWithoutNameInputs.indexOf(report.type.id) > -1 ? null : report.name;
    const subtitle =
      report.meta.subtitle ||
      (report.meta.period && report.meta.period.displayName);
    return (
      <NoDataCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        priorityWarnings={report.priorityWarnings}
        reportId={report.id}
        subtitle={subtitle}
        title={title}
        warnings={report.warnings}
      />
    );
  }

  renderProjectProfileCard(report) {
    return (
      <ProjectProfileCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderReadoutCard(report) {
    const {meta} = report;
    const {comparison} = meta;
    const {dataSummary} = meta;

    let periodSummaryValue = null;
    let sign = null;
    let percentChange = null;
    let lastReadingTimestamp = null;

    // API sends dataSummary only if data for the period or the
    // Comparison period exists (either or)
    if (dataSummary) {
      periodSummaryValue = dataSummary[0].value;
      const differenceSummary = dataSummary[2];

      // API sends differenceSummary only if data for both the period
      // And the comparison period exists
      if (differenceSummary) {
        percentChange = Math.abs(differenceSummary.value);
        sign = Math.sign(differenceSummary.value).toString();
        // Last reading should display lastReadingTimestamp
        // rather than period.
      } else if (dataSummary[0].lastReadingTimestamp) {
        lastReadingTimestamp = dataSummary[0].lastReadingTimestamp;
      }
    }
    const unitStringShort = getUnitStringShort(meta);

    const reverseArrowColors =
      meta.differenceColor &&
      meta.differenceColor.id ===
        ReportDifferenceColorEnum.GREEN_INC__RED_DEC.id;

    const {hasDynamicGauge, ...dynamicGaugeConfig} = meta.dynamicGaugeConfig;

    return (
      <ReadoutCard
        comparisonPeriodName={comparison ? comparison.displayName : null}
        connectDragSource={this.props.connectDragSource}
        customDecimalPlaces={meta.customDecimalPlaces}
        dataManagerUrl={meta.dataManagerUrl}
        disableConfig={this.disableConfig()}
        dynamicGaugeConfig={dynamicGaugeConfig}
        hasComparison={Boolean(comparison)}
        hasDynamicGauge={hasDynamicGauge}
        hideIcon={meta.hideIcon}
        hideTitle={meta.hideTitle}
        lastReadingTimestamp={lastReadingTimestamp}
        name={report.name}
        normalizerUnit={meta.normalizerUnit}
        numericValue={periodSummaryValue}
        percentChange={percentChange}
        periodName={meta.period ? meta.period.displayName : null}
        priorityWarnings={report.priorityWarnings}
        reportId={report.id}
        resource={meta.resource.slug}
        reverseArrowColors={reverseArrowColors}
        sign={sign}
        units={unitStringShort}
        warnings={report.warnings}
      />
    );
  }

  renderStackedBreakdownCard(report) {
    return (
      <StackedBreakdownCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderTableCard(report) {
    return (
      <TableCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderSummaryCard(report) {
    return (
      <SummaryCard
        {...report}
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        forExport={this.props.forExport}
        reportId={report.id}
      />
    );
  }

  renderTenantConsumptionCostCard(report) {
    return (
      <TenantConsumptionCostCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderTenantInfo(report) {
    return (
      <TenantInfoCard
        connectDragSource={this.props.connectDragSource}
        data={report.data.tenantData}
        disableConfig={this.disableConfig()}
        reportId={report.id}
        showTenantName={report.data.showTenantName}
      />
    );
  }

  renderTextCard(report) {
    return (
      <TextCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderTrendsCard(report) {
    return (
      <TrendsCard
        animate={this.props.animate}
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderPointTrendsCard(report) {
    if (report.meta.chartType === ReportChartTypeEnum.TABLE.id) {
      return this.renderTableCard(report);
    }
    return (
      <PointTrendsCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderYearOverYearTrendsCard(report) {
    if (
      report.meta.chartType &&
      report.meta.chartType.id === ReportChartTypeEnum.TABLE.id
    ) {
      return this.renderTableCard(report);
    }

    return (
      <YearOverYearTrendsCard
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        report={report}
      />
    );
  }

  renderBuildingAnalyticsZoneFlowCard(report) {
    const {data} = report;
    const {
      attributes,
      colorBy,
      height,
      linkLabels,
      linkOpacity,
      nodeSpacing,
      nodeWidth,
    } = report.meta;

    const links = parseZoneFlowLinks(data);
    const nodes = parseZoneFlowNodes(data);
    addColor(nodes, links, colorBy);

    return (
      <BuildingAnalyticsZoneFlowCard
        attributes={attributes}
        connectDragSource={this.props.connectDragSource}
        disableConfig={this.disableConfig()}
        height={height || undefined}
        linkLabels={linkLabels}
        linkOpacity={linkOpacity}
        links={links}
        nodeSpacing={nodeSpacing || undefined}
        nodeWidth={nodeWidth}
        nodes={nodes}
        report={report}
      />
    );
  }

  render() {
    const report = this.props.report;
    const trackingUrl = window.settings.urls.actionRecordReportView.replace(
      0,
      report.uuid,
    );
    const reportCard = this.getReportCard(report);
    return (
      <div className={'ContainerCard'}>
        <img height={0} loading={'lazy'} src={trackingUrl} width={0} />
        {reportCard}
      </div>
    );
  }

  onDashboardSetLayout() {
    this.setState({
      ...this.state,
      lastLayoutUpdate: new Date(),
    });
  }
}
